import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/features/auth/api/auth.api';
import { apiClient, registerAuthHandlers, refreshAccessToken, isDefinitiveAuthFailure } from '@/api/client';
import { tokenStore } from '@/api/tokenStore';
import { isApiError } from '@/api/errors';
import {
  REMEMBER_KEY, LAST_EMAIL_KEY, SIGNED_OUT_KEY, idleTimeoutMs, recordSignOut, clearSignOut, type SignOutReason,
} from '@/config/session';
import type { AuthUser, LoginRequest, LoginResponse, MfaRequest } from '@/types/auth';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (body: LoginRequest) => Promise<LoginResponse>;
  verifyMfa: (body: MfaRequest) => Promise<LoginResponse>;
  /** Revoke the session. `reason` drives the sign-in notice in every tab. */
  logout: (reason?: SignOutReason) => Promise<void>;
  /** Re-scope the session to another tenant and reset cached data. */
  switchTenant: (tenantId: string) => Promise<void>;
  /** Silently refresh the access token (used by the session keep-alive). */
  refreshSession: () => Promise<void>;
  /** Permission helpers (backend is authoritative; these gate UI only). */
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Back-off for transient failures while restoring a session on page load. */
const BOOTSTRAP_RETRY_DELAYS_MS = [500, 1500, 3000];

const normalizeUser = (user: AuthUser): AuthUser => ({
  ...user,
  displayName: user.displayName ?? user.email,
  tenantId: user.tenantId ?? '',
  roles: user.roles ?? [],
  permissions: user.permissions ?? [],
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const bootstrapped = useRef(false);
  const queryClient = useQueryClient();

  const applySession = useCallback((accessToken: string | undefined, nextUser: AuthUser | undefined) => {
    if (accessToken) tokenStore.set(accessToken);
    if (nextUser) {
      setUser(nextUser);
      setStatus('authenticated');
    }
  }, []);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setStatus('unauthenticated');
    queryClient.clear(); // drop tenant-scoped cached data on sign-out
  }, [queryClient]);

  // Wire the axios interceptor's refresh/failure hooks to this context.
  useEffect(() => {
    registerAuthHandlers({ onFailure: () => clearSession() });
  }, [clearSession]);

  // Bootstrap: try to restore a session via refresh + /me on first mount.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      // Hard refresh / direct navigation: restore the session from the HttpOnly
      // refresh cookie before any protected route renders (RequireAuth waits on
      // 'loading'). Transient failures (network, 5xx, 429 — e.g. during a deploy)
      // are retried briefly; only a definitive refusal sends the user to login.
      for (let attempt = 0; ; attempt += 1) {
        try {
          await refreshAccessToken();
          const me = await authApi.me();
          setUser(normalizeUser(me));
          setStatus('authenticated');
          return;
        } catch (e) {
          if (isDefinitiveAuthFailure(e) || attempt >= BOOTSTRAP_RETRY_DELAYS_MS.length) {
            clearSession();
            return;
          }
          await new Promise((r) => setTimeout(r, BOOTSTRAP_RETRY_DELAYS_MS[attempt]));
        }
      }
    })();
  }, [clearSession]);

  const login = useCallback(async (body: LoginRequest): Promise<LoginResponse> => {
    const res = await authApi.login(body);
    // Persist the remember preference + last email for the login form.
    try {
      localStorage.setItem(REMEMBER_KEY, body.remember ? '1' : '0');
      if (body.remember) localStorage.setItem(LAST_EMAIL_KEY, body.email);
      else localStorage.removeItem(LAST_EMAIL_KEY);
    } catch { /* storage unavailable */ }
    if (!res.mfaRequired && res.accessToken) {
      clearSignOut();
      tokenStore.set(res.accessToken);
      const nextUser = res.user ?? await authApi.me();
      applySession(res.accessToken, normalizeUser(nextUser));
      return { ...res, user: nextUser };
    }
    return res;
  }, [applySession]);

  const verifyMfa = useCallback(async (body: MfaRequest): Promise<LoginResponse> => {
    const res = await authApi.verifyMfa(body);
    // Same completion as a password-only sign-in: hold the token, load the user, clear sign-out marks.
    if (!res.accessToken) throw new Error('Sign-in did not complete.');
    clearSignOut();
    tokenStore.set(res.accessToken);
    const nextUser = res.user ?? await authApi.me();
    applySession(res.accessToken, normalizeUser(nextUser));
    return { ...res, user: nextUser };
  }, [applySession]);

  const logout = useCallback(async (reason: SignOutReason = 'logout') => {
    try {
      await authApi.logout();
    } catch (e) {
      // An expired access token makes the gateway reject logout before identity
      // can revoke the session, leaving the refresh cookie behind (a later page
      // load would silently sign the user back in). Rotate once and retry; if the
      // rotation is refused the gateway has already cleared the cookie.
      if (isApiError(e) && e.status === 401) {
        try {
          await refreshAccessToken();
          await authApi.logout();
        } catch { /* session already unusable */ }
      }
    } finally {
      // Tell every other open tab (storage event) so they sign out together.
      recordSignOut(reason, reason === 'idle' ? idleTimeoutMs() : undefined);
      clearSession();
    }
  }, [clearSession]);

  // Another tab signed out (explicitly or for inactivity): the shared session is
  // gone, so drop local state too. RequireAuth then shows the sign-in page.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SIGNED_OUT_KEY && e.newValue) clearSession();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [clearSession]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const res = await authApi.switchTenant(tenantId);
    if (!res.accessToken) throw new Error('Tenant switch did not return an access token');
    tokenStore.set(res.accessToken);
    const nextUser = await authApi.me();
    applySession(res.accessToken, normalizeUser(nextUser));
    // The token is now scoped to a different tenant → all cached data is stale.
    queryClient.clear();
  }, [applySession, queryClient]);

  const refreshSession = useCallback(async () => {
    await refreshAccessToken();
  }, []);

  const hasPermission = useCallback((p: string) => user?.permissions?.includes(p) ?? false, [user]);
  const hasAnyPermission = useCallback((ps: string[]) => ps.some((p) => user?.permissions?.includes(p)) ?? false, [user]);
  const hasAllPermissions = useCallback((ps: string[]) => ps.every((p) => user?.permissions?.includes(p)) ?? false, [user]);
  const hasRole = useCallback((r: string) => user?.roles?.includes(r) ?? false, [user]);

  // Refresh the cached user when the tab regains focus while authenticated.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const onFocus = () => {
      authApi.me().then((me) => setUser(normalizeUser(me))).catch(() => { /* interceptor handles auth loss */ });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [status]);

  const value = useMemo(
    () => ({
      status, user, login, verifyMfa, logout, switchTenant, refreshSession,
      hasPermission, hasAnyPermission, hasAllPermissions, hasRole,
    }),
    [status, user, login, verifyMfa, logout, switchTenant, refreshSession, hasPermission, hasAnyPermission, hasAllPermissions, hasRole],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { apiClient };
